import React from 'react';
import {
  Box,
  Button,
  InputLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { NumericFormat } from 'react-number-format';
import { cx } from '../../helpers/global';

interface Props {
  name: string;
  label: string;
  placeholder: string | undefined;
  values: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
  disabled: boolean;
  hidden: boolean;
  setFieldTouched?: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined,
  ) => void;
  tooltip?: string;
}

export default function DollarInput({
  name,
  label,
  placeholder,
  values,
  setFieldValue,
  disabled,
  hidden,
  setFieldTouched,
  tooltip,
}: Props) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [, { error, touched }, { setTouched }] = useField(name);
  // An empty value drops this key from values, so Formik's rebuilt touched
  // object can miss it on submit — treat any submit attempt as touched
  const { submitCount } = useFormikContext();
  const isError = (touched || submitCount > 0) && !!error;
  const onBlur = React.useCallback(() => {
    setTouched(true);
  }, [setTouched]);
  return (
    <div className={cx('input', hidden && 'hidden', isError && 'error')}>
      <InputLabel
        htmlFor={name}
        sx={{
          display: 'flex',
          justifyContent: isSmall ? 'flex-start' : 'space-between',
          alignItems: 'center',
          gap: 1,
        }}>
        <Typography variant="h5">{label}</Typography>
        {!!tooltip && (
          <Tooltip
            title={tooltip}
            arrow
            placement="top"
            enterTouchDelay={0}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -5],
                    },
                  },
                ],
              },
            }}>
            <Box
              component="span"
              sx={{
                my: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}>
              <Button
                type="button"
                variant="text"
                color="inherit"
                disableRipple
                sx={{
                  width: 'auto',
                  minWidth: 0,
                  padding: 0,
                  ':hover': {
                    backgroundColor: 'transparent',
                  },
                }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: isSmall ? 16 : 12 }}
                  fontSize="small"
                />
              </Button>
            </Box>
          </Tooltip>
        )}
      </InputLabel>
      <NumericFormat
        placeholder={placeholder}
        thousandSeparator=","
        allowNegative={false}
        decimalScale={2}
        prefix="$"
        value={values[name]}
        onValueChange={val => {
          setFieldValue(name, val.floatValue);
          if (setFieldTouched) {
            setTimeout(() => setFieldTouched(name, true), 0);
          }
        }}
        disabled={disabled}
        onBlur={onBlur}
      />
    </div>
  );
}
